import { useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';

interface ChefAnswer {
    questionId: string;
    question: string;
    answer: string;
    answeredAt: string;
}

interface ChefAnswerNotificationsProps {
    restaurantId: string;
    customerSessionId: string | null;
    enabled?: boolean;
}

/**
 * ChefAnswerNotifications - Polls for chef responses and shows popup notifications
 * 
 * When a customer asks a question via Lelah that goes to the chef,
 * this component polls for the chef's response and displays it as a toast notification.
 * Works even when Lelah is turned off - customer will see popup notifications.
 */
export function ChefAnswerNotifications({
    restaurantId,
    customerSessionId,
    enabled = true
}: ChefAnswerNotificationsProps) {
    const { toast } = useToast();
    const lastCheckTimeRef = useRef<string | null>(null);
    const shownAnswerIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        // Don't poll if no session ID or disabled
        if (!enabled || !customerSessionId) {
            return;
        }

        // Poll every 5 seconds for chef answers
        const pollForAnswers = async () => {
            try {
                const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
                const url = new URL(`${apiBaseUrl}/api/public/chef-answers/${customerSessionId}`);

                // Only get answers since last check (to avoid showing duplicates)
                if (lastCheckTimeRef.current) {
                    url.searchParams.set('since', lastCheckTimeRef.current);
                }

                const response = await fetch(url.toString());
                if (!response.ok) {
                    console.error('[ChefAnswerNotifications] Failed to fetch answers:', response.status);
                    return;
                }

                const data = await response.json();
                const answers: ChefAnswer[] = data.answers || [];

                // Show toast for each new answer
                for (const answer of answers) {
                    // Skip if we've already shown this answer
                    if (shownAnswerIdsRef.current.has(answer.questionId)) {
                        continue;
                    }

                    // Mark as shown
                    shownAnswerIdsRef.current.add(answer.questionId);

                    // Show notification toast
                    toast({
                        title: "🍳 Chef's Response",
                        description: (
                            <div className="space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    <strong>Your question:</strong> {answer.question}
                                </p>
                                <p className="text-sm font-medium">{answer.answer}</p>
                            </div>
                        ),
                        duration: 10000, // Show for 10 seconds
                    });

                    console.log('[ChefAnswerNotifications] Showed notification for answer:', answer.questionId);
                }

                // Update last check time
                lastCheckTimeRef.current = new Date().toISOString();
            } catch (error) {
                console.error('[ChefAnswerNotifications] Error polling for answers:', error);
            }
        };

        // Initial check
        pollForAnswers();

        // Set up polling interval
        const intervalId = setInterval(pollForAnswers, 5000);

        return () => {
            clearInterval(intervalId);
        };
    }, [enabled, customerSessionId, toast]);

    // This is a "headless" component - no UI rendered
    return null;
}
